import { type NavigationGuardReturn, type RouteLocationNormalizedLoaded, type RouteLocationNormalized, START_LOCATION } from 'vue-router'
import configResolver, { pathCombine } from '@/common/helpers/configResolver'
import { triggerOidcChangePassword } from '@/common/services/AuthenticationService'
import { type NavigationGuardWithoutNext } from '@/app/types/router'

import { useContextualSearchStore } from '@/search/stores/contextualSearchStore'
import { useSessionStore } from '@/common/stores/sessionStore'
import { useBreaksStore } from '@/agent/stores/breakStore'
import { useScreenStore } from '@/agent/stores/screenStore'
import { useSearchStore } from '@/search/stores/searchStore'
import { usePreBreakStore } from '@/agent/stores/preBreakStore'

import { clear as clearLocalData } from '@/common/services/LocalStorageService'
import { checkNavigationConsistent, handleNavigation, NAVIGATION_HISTORY_KEY, resetNavigationHistory } from '@/common/services/NavigationService'
import localizationServerData from '@/common/services/serverData/localizationServerData'

import { PrefixTranslationsEnum } from '@/common/types/i18n'

async function handleChangePasswordRoute(originPath: string, oidcEnabled: boolean) {
  if (!oidcEnabled) return

  await triggerOidcChangePassword(pathCombine([configResolver.root, originPath]))
}

export const contextualSearchBeforeEnter = async (): Promise<NavigationGuardReturn> => {
  useContextualSearchStore().resetContextualSearch()
}

export const changePasswordRouteBeforeEnter = async (
  _: RouteLocationNormalized,
  from: RouteLocationNormalizedLoaded,
): Promise<NavigationGuardReturn> => {
  const sessionStore = useSessionStore()
  await handleChangePasswordRoute(from.path, sessionStore.oidcEnabled)
}

export const agentBeforeEnter: NavigationGuardWithoutNext = async (_, from) => {
  if (from.meta.engage || from.meta.agent) return

  clearLocalData(['session', 'parameters', NAVIGATION_HISTORY_KEY])

  if (from?.meta?.admin) {
    const sessionStore = useSessionStore()
    sessionStore.loadSessionSettings()

    const searchStore = useSearchStore()
    searchStore.$reset()
  }

  const hydrationRequests = [
    useScreenStore().setCustomerRecordScreen(),
    useBreaksStore().load(),
    usePreBreakStore().load(),
    localizationServerData.fetch({
      prefixes: [
        PrefixTranslationsEnum.USERSTATE,
        PrefixTranslationsEnum.SCREEN,
        PrefixTranslationsEnum.SKILL,
        PrefixTranslationsEnum.SPACE,
        PrefixTranslationsEnum.REASON,
      ],
    }),
  ]
  await Promise.all(hydrationRequests)
}

export const navigationBeforeResolve: NavigationGuardWithoutNext = async (to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded) => {
  if (from.meta?.admin || to.meta?.admin || from === START_LOCATION || from.path === to.path) return

  if (to.meta?.allowBackNavigation) {
    if (!from.meta?.skipHistory) {
      handleNavigation(from.path)
    }
    checkNavigationConsistent(to.path)
  } else {
    resetNavigationHistory()
  }
}
